import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, effect, EventEmitter, inject, Input, OnInit, Output, signal, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep, isNil } from 'lodash';
import {
  CheckboxComponent,
  CheckboxDescription,
  LoaderComponent,
  ModalService,
  TileComponent,
  TreeAppendDirective,
  TreeComponent,
  TreeElement
} from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AdminPermissionPresetsFacade } from 'mbs-ui-shared@facades/admin-permission-presets.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { AccountTypeEnum, AdministratorPermissions, Permissions, PermissionsEnum } from 'mbs-ui-shared@models';
import { McpAccessLevel } from 'mbs-ui-shared@models/McpAccessLevel.enum';
import {
  AdministratorChildPermissions,
  AdministratorPermissionChildren,
  AdministratorPermissionsContext,
  isAllowedAdministratorChildPermission,
  isAllowedAdministratorPermission
} from 'mbs-ui-shared@utils/administrator-permissions.util';
import { catchError, from, map, of, take } from 'rxjs';

const MIN_PERMISSIONS_COUNT = 4;

export enum PermissionBlocksEnum {
  Backup = 'Backup',
  Products = 'Products',
  Organization = 'Organization',
  Management = 'Management',
  Reporting = 'Reporting',
  Miscellaneous = 'Miscellaneous',
  Mcp = 'Mcp'
}

type CheckboxDataByBlock = Partial<Record<keyof typeof PermissionBlocksEnum, Array<TreeElement>>>;

@Component({
  selector: 'mbs-permissions-tree',
  templateUrl: './permissions-tree.component.html',
  styleUrls: ['./permissions-tree.component.scss'],
  imports: [
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    I18NextPipe,
    LoaderComponent,
    RouterLink,
    TileComponent,
    TreeAppendDirective,
    TreeComponent
  ]
})
export class PermissionsTreeComponent implements OnInit, AfterViewInit {
  private i18N = inject(I18NextPipe);
  private auth = inject(AuthFacade);
  private modal = inject(ModalService);
  private presetsFacade = inject(AdminPermissionPresetsFacade);
  private ability = inject(AbilityService);

  @Input() accountType: AccountTypeEnum;
  @Input() showTiles = true;
  // Two trees can live in the document at once (the tab behind an open preset modal), and their
  // shared DOM ids would make every `label[for]` toggle the checkbox of whichever renders first
  @Input() idPrefix = '';

  @Input() set globalMcpLevel(level: McpAccessLevel) {
    this.myGlobalMcpLevel = level;

    if (!this.myPermissions) return;

    this.myPermissions = this.clampMcpByGlobal({
      ...this.myPermissions,
      [PermissionsEnum.McpReadAccess]:
        this.rawPermissions?.[PermissionsEnum.McpReadAccess] ?? this.myPermissions[PermissionsEnum.McpReadAccess],
      [PermissionsEnum.McpFullAccess]:
        this.rawPermissions?.[PermissionsEnum.McpFullAccess] ?? this.myPermissions[PermissionsEnum.McpFullAccess]
    });
    this.updateData(this.myPermissions);
  }

  get globalMcpLevel(): McpAccessLevel {
    return this.myGlobalMcpLevel;
  }

  // The beta default matches the spec until the provider-level setting gets a backend source
  private myGlobalMcpLevel: McpAccessLevel = McpAccessLevel.ReadOnly;
  private rawPermissions: Permissions;

  @Input() set permissions(value: Permissions) {
    // Skip the value this component has just emitted, otherwise the tree is rebuilt on every checkbox click
    if (value === this.emittedPermissions) return;

    this.rawPermissions = value;
    this.myPermissions = this.clampMcpByGlobal(value);
    this.updateData(this.myPermissions);
  }

  @Output() permissionsChange = new EventEmitter<Permissions>();

  // The parent form keeps the raw incoming value; this is the displayed state, with the MCP clamp applied
  public get currentPermissions(): Permissions {
    return this.myPermissions;
  }

  public readonly permissionDescriptions = this.getPermissionsDescriptions();
  public readonly PermissionBlocksEnum = PermissionBlocksEnum;
  public readonly PermissionsEnum = PermissionsEnum;
  public readonly RoutingPath = RoutingPath;
  public readonly elementSelectors = {
    id: {
      backup: 'permissions-block-backup',
      products: 'permissions-block-products',
      organization: 'permissions-block-organization',
      management: 'permissions-block-management',
      reporting: 'permissions-block-reporting',
      miscellaneous: 'permissions-block-miscellaneous',
      mcp: 'permissions-block-mcp'
    }
  };

  public readonly blocks: Array<{ key: PermissionBlocksEnum; label: string; e2e: string; badge?: string }> = [
    { key: PermissionBlocksEnum.Backup, label: 'Backup', e2e: this.elementSelectors.id.backup },
    { key: PermissionBlocksEnum.Products, label: 'Products', e2e: this.elementSelectors.id.products },
    { key: PermissionBlocksEnum.Organization, label: 'Organization', e2e: this.elementSelectors.id.organization },
    { key: PermissionBlocksEnum.Management, label: 'Management', e2e: this.elementSelectors.id.management },
    { key: PermissionBlocksEnum.Reporting, label: 'Reporting', e2e: this.elementSelectors.id.reporting },
    { key: PermissionBlocksEnum.Miscellaneous, label: 'Miscellaneous', e2e: this.elementSelectors.id.miscellaneous },
    {
      key: PermissionBlocksEnum.Mcp,
      label: this.i18N.transform('shared:administrators.mcpPermissions.readAccess'),
      e2e: this.elementSelectors.id.mcp,
      badge: 'New'
    }
  ];

  public readonly checkboxData = signal<CheckboxDataByBlock>({});

  protected readonly labelsLoading = computed(() => this.presetsFacade.loading() && !this.presetsFacade.permissions().length);

  private blocksStructure: Record<keyof typeof PermissionBlocksEnum, Array<PermissionsEnum>> = {
    Backup: [
      PermissionsEnum.RemoteDeploy,
      PermissionsEnum.Monitoring,
      PermissionsEnum.OnlineAccess,
      PermissionsEnum.Package, // Storage Limits
      PermissionsEnum.Storage,
      PermissionsEnum.ShowBackupRestorePlansAndAgentSettings,
      PermissionsEnum.CloudRestore
    ],
    Products: [
      PermissionsEnum.GoogleApps,
      PermissionsEnum.Rmm,
      PermissionsEnum.ShowTickets,
      PermissionsEnum.ShowRemoteConnection,
      PermissionsEnum.ShowDeepInstinct
    ],
    Organization: [
      PermissionsEnum.Users,
      PermissionsEnum.Licenses,
      PermissionsEnum.PurchaseHistory,
      PermissionsEnum.ManageCompanies,
      PermissionsEnum.Administrator,
      PermissionsEnum.AuditLog
    ],
    Management: [
      PermissionsEnum.DeleteDataFromStorage,
      PermissionsEnum.RemoteManagement,
      PermissionsEnum.AssignTags,
      PermissionsEnum.Downloads
    ],
    Reporting: [
      PermissionsEnum.Dashboard,
      PermissionsEnum.BackupHistory,
      PermissionsEnum.StorageUsage,
      PermissionsEnum.CapacityReport,
      PermissionsEnum.Billing,
      PermissionsEnum.Notification
    ],
    Miscellaneous: [
      PermissionsEnum.PSA,
      PermissionsEnum.ADS,
      PermissionsEnum.HelpMarketing,
      PermissionsEnum.ShowMSP360ControlMobileApplication
    ],
    Mcp: [PermissionsEnum.McpFullAccess]
  };
  // Why siblings can be dependent? Just because...
  // Key in structure is dependent, value is master, this is faster than reverse
  private dependentSiblings = {
    [PermissionsEnum.ManageStorageLimitsForCompany]: PermissionsEnum.AddOrRemoveDestinationForCompany,
    [PermissionsEnum.ManageStorageLimitsForUser]: PermissionsEnum.AddOrRemoveDestinationForUser
  };

  private attachedChildren = {
    ShowRemoteConnection: [PermissionsEnum.DirectConnectAccess],
    RemoteManagement: [PermissionsEnum.QuickRestore, PermissionsEnum.AgentlessRestore]
  };
  private allCheckboxData: TreeElement[] = [];

  private DOMIsReady = false;

  private myPermissions: Permissions;
  private emittedPermissions: Permissions;

  // Backup and Mcp headers are their own permissions, the rest of the blocks are driven by their children
  public blockChecked(block: PermissionBlocksEnum): boolean {
    if (block === PermissionBlocksEnum.Backup) return !!this.myPermissions?.[PermissionsEnum.Backup];
    if (block === PermissionBlocksEnum.Mcp) return !!this.myPermissions?.[PermissionsEnum.McpReadAccess];

    return this.allPermissionsChecked(block);
  }

  public blockIndeterminate(block: PermissionBlocksEnum): boolean {
    return block === PermissionBlocksEnum.Backup || block === PermissionBlocksEnum.Mcp ? false : this.indeterminatePermissions(block);
  }

  public blockDisabled(block: PermissionBlocksEnum): boolean {
    return block === PermissionBlocksEnum.Mcp && this.globalMcpLevel === McpAccessLevel.Disabled;
  }

  // Shown only while the provider-level setting makes some of the section unavailable
  public get mcpGlobalNote(): string | null {
    const note = { [McpAccessLevel.Disabled]: 'globalNoteDisabled', [McpAccessLevel.ReadOnly]: 'globalNoteReadOnly' }[this.globalMcpLevel];

    if (!note) return null;

    return this.i18N.transform(`shared:administrators.mcpPermissions.${note}${this.mcpNoteSettingsLink ? 'Provider' : ''}`);
  }

  // Only the provider owns the global level, so only they get pointed at the setting itself
  public get mcpNoteSettingsLink(): boolean {
    return this.globalMcpLevel !== McpAccessLevel.Full && this.ability.can('read', PermissionsEnum.Provider);
  }

  // The clamped value reaches the form only through user interaction and is persisted by an explicit Save
  private clampMcpByGlobal(value: Permissions): Permissions {
    if (!value) return value;

    const clamped = { ...value };

    if (this.globalMcpLevel !== McpAccessLevel.Full) clamped[PermissionsEnum.McpFullAccess] = false;
    if (this.globalMcpLevel === McpAccessLevel.Disabled) clamped[PermissionsEnum.McpReadAccess] = false;

    return clamped;
  }

  private isMcpGloballyRestricted(permission: PermissionsEnum | string): boolean {
    if (permission === PermissionsEnum.McpReadAccess) return this.globalMcpLevel === McpAccessLevel.Disabled;
    if (permission === PermissionsEnum.McpFullAccess) return this.globalMcpLevel !== McpAccessLevel.Full;

    return false;
  }

  private get permissionLabels(): Partial<Record<PermissionsEnum, string>> {
    // The MCP pair keeps the approved mockup wording even when the backend dictionary carries its own labels
    return {
      ...this.presetsFacade.permissionLabels(),
      [PermissionsEnum.McpReadAccess]: this.i18N.transform('shared:administrators.mcpPermissions.readAccess'),
      [PermissionsEnum.McpFullAccess]: this.i18N.transform('shared:administrators.mcpPermissions.fullAccess')
    };
  }

  constructor() {
    // Labels arrive from the backend, so the already built tree has to pick them up
    effect(() => {
      this.presetsFacade.permissionLabels();
      untracked(() => this.myPermissions && this.updateData(this.myPermissions));
    });
  }

  ngOnInit(): void {
    this.presetsFacade
      .init()
      .pipe(
        catchError(() => of(null)),
        take(1)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.DOMIsReady = true;
  }

  // A preset can carry permissions the current tree does not offer (hidden by ability or account type) - those are left untouched
  public applyPermissions(presetPermissions: PermissionsEnum[]): void {
    const enabled = new Set(presetPermissions);
    const permissions = Object.assign({}, this.myPermissions);

    if (this.checkboxData().Backup?.length) {
      permissions[PermissionsEnum.Backup] = enabled.has(PermissionsEnum.Backup);
    }

    if (this.checkboxData().Mcp?.length) {
      permissions[PermissionsEnum.McpReadAccess] = enabled.has(PermissionsEnum.McpReadAccess);
    }

    this.allCheckboxData.forEach((node) => {
      permissions[node.id] = enabled.has(node.id as PermissionsEnum);
      node.children?.forEach((child) => (permissions[child.id] = enabled.has(child.id as PermissionsEnum)));
    });

    this.myPermissions = this.clampMcpByGlobal(permissions);
    this.updateData(this.myPermissions);
    this.emitPermissions();
  }

  allPermissionsChecked(block?: PermissionBlocksEnum): boolean {
    const { all, checked } = this.getCheckboxesState(block);

    return all === checked;
  }

  indeterminatePermissions(block?: PermissionBlocksEnum): boolean {
    const { all, checked } = this.getCheckboxesState(block);

    return 0 < checked && checked < all;
  }

  private getCheckboxesState(block?: PermissionBlocksEnum): { all: number; checked: number } {
    let all = 0;
    let checked = 0;
    const data = block ? this.checkboxData()[block] : this.allCheckboxData;

    const calculateCount = (element: TreeElement) => {
      all += 1;
      element.checked && (checked += 1);
    };

    data.forEach((element) => {
      calculateCount(element);

      element.children?.forEach((el) => calculateCount(el));
    });

    return { all, checked };
  }

  updateData(value): void {
    if (!value) return;

    const valueKeys = Object.keys(value);

    if (this.needRefillData(valueKeys)) {
      this.generateCheckboxData(Object.keys(AdministratorPermissions));
    } else {
      this.generateCheckboxData(valueKeys, value);
    }
  }

  generateCheckboxData(keys, value = null): void {
    this.setCheckboxData([]);
    const resultArray = [];

    const isReadonlyPermission = (id: PermissionsEnum): boolean => {
      return this.myPermissions?.[this.dependentSiblings?.[id]];
    };

    keys.forEach((key: string) => {
      // Internal permission
      if (key === PermissionsEnum.UseOldConnectSettings || key === PermissionsEnum.McpReadAccess) {
        return;
      }

      const withChild = AdministratorPermissionChildren[key];
      const elementIsChild = AdministratorChildPermissions.some((el) => el === key);
      const allowedElement = isAllowedAdministratorPermission(key as PermissionsEnum, this.permissionsContext.can);
      let checkboxDataItem = null;
      if (!elementIsChild && allowedElement) {
        checkboxDataItem = {
          label: this.permissionLabels[key] || key,
          id: key,
          value: value?.[key],
          checked: value?.[key],
          readonly: this.isMcpGloballyRestricted(key),
          children: []
        };
      }

      if (withChild?.length && checkboxDataItem) {
        checkboxDataItem.children = withChild
          .map((child) => {
            if (isAllowedAdministratorChildPermission(child as PermissionsEnum, this.permissionsContext)) {
              return {
                label: this.permissionLabels[child] || child,
                id: child,
                value: value?.[child],
                checked: value?.[child],
                children: [],
                readonly: isReadonlyPermission(child) || this.isMcpGloballyRestricted(child)
              };
            }

            return undefined;
          })
          .filter(Boolean);
      }

      if (checkboxDataItem?.children?.[0] && checkboxDataItem.children.some((c) => c.checked !== checkboxDataItem.children[0].checked)) {
        checkboxDataItem.indeterminate = true;
      }

      checkboxDataItem && resultArray.push(checkboxDataItem);
    });

    this.sortCheckBoxes(resultArray);
    this.setCheckboxData(resultArray);
  }

  private get permissionsContext(): AdministratorPermissionsContext {
    return {
      accountType: this.accountType,
      can: (permission) => this.ability.can('read', permission),
      chat: this.auth.currentUserValue?.ProviderInfo?.Chat
    };
  }

  private sortCheckBoxes(checkboxes: TreeElement[]): TreeElement[] {
    // Order in UI
    // ...
    // [] Remote Management
    // [] Remote Connection
    // ...

    const showRemoteConnectionIndex = checkboxes.findIndex(({ id }) => id === PermissionsEnum.DeleteDataFromStorage);
    const remoteManagementIndex = checkboxes.findIndex(({ id }) => id === PermissionsEnum.RemoteManagement);
    const remoteManagementElement = checkboxes.splice(remoteManagementIndex, 1)[0];

    return checkboxes.splice(showRemoteConnectionIndex, 0, remoteManagementElement);
  }

  handleSelectAll(event: boolean, block?: PermissionBlocksEnum): void {
    if (!block || block === PermissionBlocksEnum.Backup) {
      this.myPermissions = Object.assign({}, this.myPermissions, { [PermissionsEnum.Backup]: event });
    }

    if (!block || block === PermissionBlocksEnum.Mcp) {
      this.myPermissions = Object.assign({}, this.myPermissions, {
        [PermissionsEnum.McpReadAccess]: event && this.globalMcpLevel !== McpAccessLevel.Disabled
      });
    }

    const cascadeToChildren = !((block === PermissionBlocksEnum.Backup || block === PermissionBlocksEnum.Mcp) && event);
    const checkboxData = cloneDeep(this.allCheckboxData);

    checkboxData.forEach((permission) => {
      if (block && !this.blocksStructure[block].includes(permission.id as PermissionsEnum)) {
        return;
      }

      permission.indeterminate = false;

      if (cascadeToChildren) {
        if (!(event && this.isMcpGloballyRestricted(permission.id))) permission.checked = event;
        permission.children?.forEach((childPermission) => {
          if (!(event && this.isMcpGloballyRestricted(childPermission.id))) childPermission.checked = event;
        });
      }
    });

    this.setCheckboxData(checkboxData);
    this.updateMyPermissions();
  }

  treeChangeHandler(permission: TreeElement, value?: boolean): void {
    if (!isNil(value)) permission.checked = value;

    const updatePermissions = () => {
      if (!permission.checked) {
        permission.children?.forEach((childNode: TreeElement) => {
          this.treeChangeHandler(childNode, false);
        });
      }

      if (permission.checked && this.attachedChildren[permission.id]) {
        permission.children?.forEach((childNode: TreeElement) => {
          if (this.attachedChildren[permission.id].includes(childNode.id)) {
            this.treeChangeHandler(childNode, true);
          }
        });
      }

      this.refreshIndeterminate(permission.parent ?? permission);

      this.DOMIsReady && this.updateMyPermissions(permission);
    };

    if (
      !this.ability.can('read', PermissionsEnum.RmmPerEndpoint) &&
      this.ability.can('read', PermissionsEnum.RmmCeEnabled) &&
      this.ability.can('read', PermissionsEnum.RmmPerAdminLicenseExists) &&
      permission.id === PermissionsEnum.Rmm &&
      !permission.checked
    ) {
      from(
        this.modal.confirm(
          {
            header: { title: this.i18N.transform('shared:administrators.rmmPermissionConfirmCaption') },
            footer: { okButton: { text: this.i18N.transform('shared:buttons.proceed') } }
          },
          this.i18N.transform('shared:administrators.rmmPermissionConfirmText')
        )
      )
        .pipe(
          take(1),
          catchError(() => of(false)),
          map((result) => (result ? true : false))
        )
        .subscribe((res) => {
          !res && (permission.checked = true);
          updatePermissions();
          this.setCheckboxData(this.allCheckboxData);
        });
    } else {
      if (
        permission.id === PermissionsEnum.AddOrRemoveDestinationForUser ||
        permission.id === PermissionsEnum.AddOrRemoveDestinationForCompany
      ) {
        // Dirty hack
        this.updateLimitPermissionState(permission);
      }

      updatePermissions();
    }
  }

  private refreshIndeterminate(node: TreeElement): void {
    if (!node?.children?.length) return;

    node.indeterminate = node.children.some((child) => child.checked !== node.children[0].checked);
  }

  private updateLimitPermissionState(changedSibling: TreeElement) {
    const limitPermissionId =
      changedSibling.id === PermissionsEnum.AddOrRemoveDestinationForUser
        ? PermissionsEnum.ManageStorageLimitsForUser
        : PermissionsEnum.ManageStorageLimitsForCompany;

    changedSibling.parent.children.forEach((sibling) => {
      if (sibling.id !== limitPermissionId) {
        return;
      }

      if (changedSibling.checked) {
        sibling.checked = true;
      }

      sibling.readonly = changedSibling.checked;

      this.updateMyPermissions(sibling);
    });
  }

  updateMyPermissions(permission = null): void {
    const newPermissions = Object.assign({}, this.myPermissions);
    const iterationObject = !permission ? this.allCheckboxData : [permission];

    iterationObject.forEach((checkboxPermission) => {
      newPermissions[checkboxPermission.id] = checkboxPermission.checked;
      checkboxPermission.children?.forEach((child) => (newPermissions[child.id] = child.checked));
    });

    this.myPermissions = Object.assign({}, newPermissions);
    this.emitPermissions();
  }

  private emitPermissions(): void {
    this.emittedPermissions = this.myPermissions;
    this.permissionsChange.emit(this.myPermissions);
  }

  private getPermissionsDescriptions(): { [key: string]: string } {
    const map = [
      PermissionsEnum.ADS,
      PermissionsEnum.CustomizeDashboard,
      PermissionsEnum.ManageImmutability,
      PermissionsEnum.ManageTags,
      PermissionsEnum.McpReadAccess,
      PermissionsEnum.OnlineAccess,
      PermissionsEnum.PSA,
      PermissionsEnum.Rebranding,
      PermissionsEnum.SecurityWidget,
      PermissionsEnum.BackupHistory,
      PermissionsEnum.AddOrRemoveDestinationForCompany,
      PermissionsEnum.AddOrRemoveDestinationForUser,
      PermissionsEnum.StorageUsage,
      PermissionsEnum.CapacityReport,
      PermissionsEnum.Notification
    ];

    return map.reduce((acc, current) => {
      return { ...acc, [current]: this.i18N.transform('shared:permissionsDescriptions.' + current) };
    }, {});
  }

  private setCheckboxData(data: TreeElement[]): void {
    const collection: Record<string, TreeElement> = data.reduce((result, current) => {
      result[current.id] = current;

      return result;
    }, {});

    this.allCheckboxData = data;

    const backupDisabled = !this.myPermissions?.[PermissionsEnum.Backup];

    const checkboxData: CheckboxDataByBlock = {};

    Object.keys(this.blocksStructure).forEach((block: keyof typeof PermissionBlocksEnum) => {
      checkboxData[block] = [];

      (this.blocksStructure[block] as Array<string>).forEach((key) => {
        if (!isNil(collection[key])) {
          if (block === PermissionBlocksEnum.Backup) {
            collection[key].readonly = backupDisabled;
            collection[key].children?.forEach((child) => (child.readonly = backupDisabled));
          }

          if (block === PermissionBlocksEnum.Mcp) {
            collection[key].readonly = !this.myPermissions?.[PermissionsEnum.McpReadAccess] || this.isMcpGloballyRestricted(key);
          }

          checkboxData[block].push(collection[key]);
        }
      });
    });

    this.checkboxData.set(checkboxData);
  }

  private needRefillData(valueKeys: Array<string>): boolean {
    // The case for which this was done was forgotten, and it's still scary to delete.
    return valueKeys.length <= MIN_PERMISSIONS_COUNT;
  }
}
